/**
 * `F32: F32Api` — f32 emulation in Phira's operation order (W1-01 core-time-math; rpe-format.md F§6.2-6.3,
 * DESIGN.md §6.2, ARCHITECTURE.md §7.1 numeric policy).
 *
 * Every operation rounds its operands to f32 first and its result after: for f32 inputs, one f64 operation
 * followed by `Math.fround` is the correctly rounded f32 result (+, -, *, /), so these helpers are bit-exact
 * with Rust's f32 arithmetic. Rounding the operands makes callers that pass f64 values (JSON numbers, factors
 * computed in f64) behave as Phira, which stores every keyframe value as f32.
 */

import type { F32Api } from './types';

const fr = Math.fround;

export const F32: F32Api = {
  fr,
  add: (a, b) => fr(fr(a) + fr(b)),
  sub: (a, b) => fr(fr(a) - fr(b)),
  mul: (a, b) => fr(fr(a) * fr(b)),
  div: (a, b) => fr(fr(a) / fr(b)),
  // Phira keyframe interpolation `x + (y - x) * t` in f32: fround(a + fround(fround(b - a) * y)).
  lerp(a, b, y) {
    const a32 = fr(a);
    return fr(a32 + fr(fr(fr(b) - a32) * fr(y)));
  },
  // Right-associative chained sum: v[0] + (v[1] + (v[2] + ...)), every addition rounded to f32.
  sumChain(v, count) {
    const n = Math.min(count, v.length);
    if (n <= 0) return 0;
    let acc = fr(v[n - 1] ?? 0);
    for (let i = n - 2; i >= 0; i--) acc = fr(fr(v[i] ?? 0) + acc);
    return acc;
  },
};

/** Property factors exactly as Phira computes them in f32 (DESIGN.md §6.2 step 1). */
export const F32_FACTORS = Object.freeze({
  /** Alpha: 0..255 -> 0..1. */
  alpha: fr(1 / 255),
  /** moveX: RPE px -> world units (x in [-675, 675] -> [-1, 1]). */
  moveX: fr(2 / 1350),
  /** moveY: RPE px -> world units before the aspect-ratio division (y in [-450, 450] -> [-1, 1]). */
  moveY: fr(2 / 900),
  /** rotate: RPE clockwise degrees -> counter-clockwise. */
  rotate: -1,
});

/** `fround(v * factor)` with both operands rounded to f32: a keyframe value pre-multiplied by its factor. */
export function factorF32(v: number, factor: number): number {
  return fr(fr(v) * fr(factor));
}

/** Rounds every element of `src` (first `count`) to f32 into `out` (may alias `src`). Returns `out`. */
export function roundArrayF32<T extends Float32Array | Float64Array | number[]>(
  src: ArrayLike<number>,
  out: T,
  count = src.length,
): T {
  const n = Math.min(count, src.length);
  for (let i = 0; i < n; i++) out[i] = fr(src[i] ?? 0);
  return out;
}

/** True when x is exactly representable as an f32 (NaN counts as representable). */
export function isF32(x: number): boolean {
  return Number.isNaN(x) || fr(x) === x;
}
